import React, { useState, useEffect } from 'react';
import { getFacturas, getDashboardStats, getFurgones } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

function StatCard({ label, value, color, icon }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ position: 'absolute', right: 18, top: 18, fontSize: 28, opacity: 0.15 }}>{icon}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', margin: '6px 0 2px' }}>{value}</div>
    </div>
  );
}

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLOR_ESTADO = { 'Pendiente': '#d97706', 'Abono Parcial': '#1d4ed8', 'Pagada': '#16a34a', 'Anulada': '#cc0000' };

const badgeStyle = (estado) => {
  const map = {
    'Pendiente': { bg: '#fef3c7', color: '#d97706' },
    'Abono Parcial': { bg: '#dbeafe', color: '#1d4ed8' },
    'Pagada': { bg: '#dcfce7', color: '#16a34a' },
    'Anulada': { bg: '#fee2e2', color: '#cc0000' },
  };
  const s = map[estado] || { bg: '#f1f5f9', color: '#64748b' };
  return { background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600 };
};

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recientesData, setRecientesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fechaFiltro, setFechaFiltro] = useState('');
  const [filtroRapido, setFiltroRapido] = useState('hoy');
  const [facturasFiltro, setFacturasFiltro] = useState([]);
  const [loadingFiltro, setLoadingFiltro] = useState(false);
  const [furgones, setFurgones] = useState([]);
  const [furgonFiltro, setFurgonFiltro] = useState('');

  useEffect(() => {
    getFurgones().then(res => setFurgones(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([getDashboardStats(furgonFiltro), getFacturas(1, 5, '', '', '', furgonFiltro)])
      .then(([statsRes, recientesRes]) => {
        setStats(statsRes.data);
        setRecientesData(recientesRes.data.data || []);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [furgonFiltro]);

  useEffect(() => {
    setLoadingFiltro(true);
    getFacturas(1, 300, '', '', '', furgonFiltro).then(res => {
      setFacturasFiltro(res.data.data || []);
      setLoadingFiltro(false);
    }).catch(() => setLoadingFiltro(false));
  }, [furgonFiltro]);

  if (loading || !stats) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Cargando...</div>;

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const thisMonth = today.slice(0, 7);
  const thisYear = today.slice(0, 4);

  const getTotal = (f) => f.detalles?.reduce((s, d) => s + d.total, 0) || 0;

  const inicioSemana = new Date(now);
  const diaSemana = inicioSemana.getDay();
  const offset = diaSemana === 0 ? 6 : diaSemana - 1;
  inicioSemana.setDate(inicioSemana.getDate() - offset);
  const inicioSemanaStr = `${inicioSemana.getFullYear()}-${String(inicioSemana.getMonth() + 1).padStart(2, '0')}-${String(inicioSemana.getDate()).padStart(2, '0')}`;

  let facturasFiltradas = [];
  let tituloFiltro = '';

  if (fechaFiltro) {
    facturasFiltradas = facturasFiltro.filter(f => f.fechaCreacion?.startsWith(fechaFiltro));
    tituloFiltro = new Date(fechaFiltro + 'T00:00:00').toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' });
  } else if (filtroRapido === 'hoy') {
    facturasFiltradas = facturasFiltro.filter(f => f.fechaCreacion?.startsWith(today));
    tituloFiltro = 'Hoy';
  } else if (filtroRapido === 'semana') {
    facturasFiltradas = facturasFiltro.filter(f => f.fechaCreacion?.split('T')[0] >= inicioSemanaStr);
    tituloFiltro = 'Esta semana';
  } else if (filtroRapido === 'mes') {
    facturasFiltradas = facturasFiltro.filter(f => f.fechaCreacion?.startsWith(thisMonth));
    tituloFiltro = 'Este mes';
  } else {
    facturasFiltradas = [...facturasFiltro];
    tituloFiltro = 'Últimas 300 facturas';
  }

  facturasFiltradas = [...facturasFiltradas].sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));

  const facturasPorMes = stats.facturasPorMes.map(m => ({
    mes: MESES[m.mes - 1],
    facturas: m.facturas,
    ingresos: m.ingresos
  }));

  const estadosPago = stats.estadosPago
    .map(e => ({ name: e.estado, value: e.cantidad, color: COLOR_ESTADO[e.estado] || '#64748b' }))
    .filter(e => e.value > 0);

  return (
    <div>
      {/* Selector de furgón */}
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#0d2461' }}>Ver estadísticas de:</span>
        <select value={furgonFiltro} onChange={e => setFurgonFiltro(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
          <option value="">Todos los furgones</option>
          {furgones.map(f => <option key={f.id} value={f.numero}>Furgón #{f.numero}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Facturas Hoy" value={stats.facturasHoy} color="#0d2461" icon="📋" />
        <StatCard label="Este Mes" value={stats.facturasMes} color="#7c3aed" icon="📅" />
        <StatCard label="Pendientes" value={stats.pendientes} color="#d97706" icon="⏳" />
        <StatCard label="Pagadas" value={stats.pagadas} color="#16a34a" icon="✅" />
        <StatCard label="En Tránsito" value={stats.enTransito} color="#0d2461" icon="🚛" />
      </div>

      {/* Ingresos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Ingresos Este Mes (USD)</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#16a34a', margin: '6px 0' }}>${stats.ingresosMesUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Balance Pendiente (USD)</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#cc0000', margin: '6px 0' }}>${stats.balancePendiente.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Filtro de facturas */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#0d2461' }}>🔍 Buscar Facturas</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { key: 'hoy', label: 'Hoy' },
            { key: 'semana', label: 'Esta Semana' },
            { key: 'mes', label: 'Este Mes' },
            { key: 'todas', label: 'Todas' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => { setFiltroRapido(key); setFechaFiltro(''); }}
              style={{
                padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                border: filtroRapido === key && !fechaFiltro ? 'none' : '1.5px solid #e2e8f0',
                background: filtroRapido === key && !fechaFiltro ? '#0d2461' : 'white',
                color: filtroRapido === key && !fechaFiltro ? 'white' : '#475569',
              }}>{label}</button>
          ))}
          <span style={{ color: '#cbd5e1' }}>|</span>
          <input type="date" value={fechaFiltro} onChange={e => setFechaFiltro(e.target.value)}
            style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
        </div>

        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
          <strong style={{ color: '#0d2461' }}>{facturasFiltradas.length}</strong> factura{facturasFiltradas.length !== 1 ? 's' : ''} — {tituloFiltro}
          {filtroRapido === 'todas' && !fechaFiltro && (
            <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>(mostrando las 300 más recientes)</span>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Factura', 'Fecha', 'Remitente', 'Destinatario', 'Furgón', 'Total', 'Estado Pago'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingFiltro ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Cargando...</td></tr>
              ) : facturasFiltradas.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>No hay facturas en este rango</td></tr>
              ) : facturasFiltradas.map(f => (
                <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{f.numero}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{new Date(f.fechaCreacion).toLocaleDateString('es-DO')}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13 }}>{f.remNombre}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13 }}>{f.desNombre}</td>
                  <td style={{ padding: '10px 12px' }}><span style={{ background: '#dbeafe', color: '#0d2461', padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 600 }}>#{f.furgonNumero}</span></td>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>${getTotal(f).toFixed(2)}</td>
                  <td style={{ padding: '10px 12px' }}><span style={badgeStyle(f.estadoPago)}>{f.estadoPago}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gráficas */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>

        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#0d2461' }}>📊 Facturas por Mes — {thisYear}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={facturasPorMes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="facturas" fill="#0d2461" radius={[4, 4, 0, 0]} name="Facturas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#0d2461' }}>🥧 Estado de Pagos</h3>
          {estadosPago.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={estadosPago} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {estadosPago.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Legend formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Ingresos por mes */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#0d2461' }}>💰 Ingresos por Mes (USD) — {thisYear}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={facturasPorMes}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos']} />
            <Bar dataKey="ingresos" fill="#16a34a" radius={[4, 4, 0, 0]} name="Ingresos USD" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recientes */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Facturas Recientes</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Factura', 'Remitente', 'Destinatario', 'Furgón', 'Total', 'Estado Pago', 'Estado Envío'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recientesData.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No hay facturas aún</td></tr>
              ) : recientesData.map(f => (
                <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '13px 16px', fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{f.numero}</td>
                  <td style={{ padding: '13px 16px', fontSize: 14 }}>{f.remNombre}</td>
                  <td style={{ padding: '13px 16px', fontSize: 14 }}>{f.desNombre}</td>
                  <td style={{ padding: '13px 16px' }}><span style={{ background: '#dbeafe', color: '#0d2461', padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600 }}>#{f.furgonNumero}</span></td>
                  <td style={{ padding: '13px 16px', fontSize: 14, fontWeight: 600 }}>${getTotal(f).toFixed(2)}</td>
                  <td style={{ padding: '13px 16px' }}><span style={badgeStyle(f.estadoPago)}>{f.estadoPago}</span></td>
                  <td style={{ padding: '13px 16px' }}><span style={badgeStyle(f.estadoEnvio)}>{f.estadoEnvio}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;